import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Brain, Plus, Trash2, TrendingUp, TrendingDown, ShieldCheck, Target,
  FileCheck, Sparkles, ArrowRight, Info, Upload
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';
import { Brain as A1, Upload as A2, BadgeCheck as A3, Target as A4, Layers as A5, FileCheck as A6 } from 'lucide-react';

import ProofOfSkill from '../components/ProofOfSkill';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAND = {
  strong: { ring: 'border-emerald-400/40 bg-emerald-400/10', text: 'text-emerald-300', bar: 'bg-emerald-400' },
  developing: { ring: 'border-amber-400/40 bg-amber-400/10', text: 'text-amber-300', bar: 'bg-amber-400' },
  missing: { ring: 'border-rose-400/40 bg-rose-400/10', text: 'text-rose-300', bar: 'bg-rose-400' },
  unknown: { ring: 'border-white/10 bg-white/[0.04]', text: 'text-slate-300', bar: 'bg-slate-500' },
};

const LAYERS = [
  ['claimed', 'Skills I say I have', 'text-cyan-300'],
  ['demonstrated', 'Skills I can demonstrate', 'text-emerald-300'],
  ['required', 'Skills my target requires', 'text-violet-300'],
  ['emerging_held', 'Skills the market wants', 'text-amber-300'],
];

const CATEGORIES = [
  ['core', 'Core'],
  ['technical', 'Technical'],
  ['human', 'Human'],
  ['credential', 'Credential'],
];

export default function SkillsPage() {
  const [arrived, setArrived] = useState(false);
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [skills, setSkills] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [target, setTarget] = useState('');
  const [gap, setGap] = useState(null);
  const [emerging, setEmerging] = useState(null);
  const [draft, setDraft] = useState({ name: '', category: 'technical', proficiency: 3, years: '' });
  const [duties, setDuties] = useState('');
  const [translation, setTranslation] = useState(null);
  const [milRef, setMilRef] = useState(null);
  const [mos, setMos] = useState('');
  const [grade, setGrade] = useState('');
  const [crosswalk, setCrosswalk] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tiles, setTiles] = useState([]);
  const [tileGroups, setTileGroups] = useState([]);
  const [pickedTiles, setPickedTiles] = useState([]);
  const [resumeText, setResumeText] = useState('');
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    axios.get(`${API}/skills/experience-tiles`, getAuthHeader())
      .then(r => { setTiles(r.data.tiles || []); setTileGroups(r.data.groups || []); })
      .catch(() => {});
  }, [getAuthHeader]);

  const applyTiles = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/skills/from-experience`, { tiles: pickedTiles }, getAuthHeader());
      setSkills(r.data.skills || []);
      setPickedTiles([]);
      toast.success(r.data.added.length ? `Added ${r.data.added.length} skills` : 'Those skills were already on your profile');
    } catch {
      toast.error('Could not add those skills');
    } finally { setBusy(false); }
  };

  const importResume = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/skills/from-resume`, { text: resumeText },
        { ...getAuthHeader(), timeout: 60000 });
      setSuggestions(r.data.suggestions || []);
      if (!r.data.suggestions?.length) toast.error('No clear skills found, try pasting more of the resume');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not read that resume');
    } finally { setBusy(false); }
  };

  const acceptSuggestions = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/skills/accept-suggestions`, { skills: suggestions }, getAuthHeader());
      setSkills(r.data.skills || []);
      setSuggestions([]);
      setResumeText('');
      toast.success(`Saved ${r.data.added.length} skills with resume evidence attached`);
      load();
    } catch {
      toast.error('Could not save those skills');
    } finally { setBusy(false); }
  };

  const load = useCallback(async () => {
    try {
      const [p, e] = await Promise.all([
        axios.get(`${API}/skills/profile`, getAuthHeader()),
        axios.get(`${API}/skills/emerging`, getAuthHeader()),
      ]);
      setSkills(p.data.profile?.skills || []);
      setTarget(p.data.profile?.target_occupation || '');
      setEvidence(p.data.evidence || []);
      setEmerging(e.data);
      try {
        const m = await axios.get(`${API}/military/reference`, getAuthHeader());
        setMilRef(m.data);
      } catch { /* optional */ }
    } catch {
      toast.error('Could not load your skills profile');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const fetchGap = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/skills/gap`, { ...getAuthHeader(), params: { target } });
      setGap(r.data);
    } catch {
      setGap(null);
    }
  }, [getAuthHeader, target]);

  useEffect(() => { if (skills.length) fetchGap(); }, [skills, evidence, fetchGap]);

  const save = async (nextSkills, nextTarget = target) => {
    setBusy(true);
    try {
      await axios.post(`${API}/skills/profile`,
        { target_occupation: nextTarget, skills: nextSkills }, getAuthHeader());
      setSkills(nextSkills);
      toast.success('Skills profile updated');
    } catch {
      toast.error('Could not save');
    } finally {
      setBusy(false);
    }
  };

  const addSkill = () => {
    if (!draft.name.trim()) return toast.error('Name the skill first');
    const next = [...skills, {
      name: draft.name.trim(), category: draft.category,
      proficiency: Number(draft.proficiency), years: draft.years ? Number(draft.years) : null,
    }];
    setDraft({ name: '', category: 'technical', proficiency: 3, years: '' });
    save(next);
  };

  const addEvidence = async (skill) => {
    const title = window.prompt(`What proves you can do "${skill}"? (project, course, credential, evaluation)`);
    if (!title) return;
    try {
      const r = await axios.post(`${API}/skills/evidence`, { skill, kind: 'project', title }, getAuthHeader());
      setEvidence([...evidence, r.data.evidence]);
      toast.success('Added to your Evidence Wallet');
    } catch {
      toast.error('Could not add evidence');
    }
  };

  const removeEvidence = async (id) => {
    try {
      await axios.delete(`${API}/skills/evidence/${id}`, getAuthHeader());
      setEvidence(evidence.filter((e) => e.id !== id));
    } catch {
      toast.error('Could not remove');
    }
  };

  const translate = async () => {
    if (!duties.trim()) return toast.error('Describe what you were responsible for');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/skills/translate-military`, { duties }, getAuthHeader());
      setTranslation(r.data);
    } catch {
      toast.error('Translation failed');
    } finally {
      setBusy(false);
    }
  };

  const runCrosswalk = async () => {
    if (!mos && !grade && !duties.trim()) return toast.error('Enter your pay grade, your MOS code, or describe your duties');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/military/crosswalk`,
        { mos_code: mos, pay_grade: grade, duties }, getAuthHeader());
      setCrosswalk(r.data);
    } catch {
      toast.error('Crosswalk failed');
    } finally {
      setBusy(false);
    }
  };

  const adoptTranslated = () => {
    const existing = new Set(skills.map((s) => s.name.toLowerCase()));
    const next = [...skills, ...(translation.civilian_skills || [])
      .filter((s) => !existing.has(s.toLowerCase()))
      .map((s) => ({ name: s, category: 'core', proficiency: 4, years: null }))];
    save(next);
    setTranslation(null);
  };

  const evidencedNames = new Set(evidence.map((e) => (e.skill || '').toLowerCase()));

  if (loading) return <AppLayout><div className="flex justify-center py-24"><LoadingSpinner /></div></AppLayout>;

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="skills-arrival"
        eyebrow="Skills Intelligence"
        title="Prove what you can already do."
        subtitle="Most people undersell themselves because nobody ever wrote their skills down properly. Here you tap what you've actually done, upload the proof you already have, and we turn it into named skills with evidence, then measure them against the job you want."
        accent="#34d399"
        second="#22d3ee"
        ctaLabel="Build my skill record"
        narration={"This is Skills Intelligence. It exists because most people undersell themselves, not out of modesty, but because nobody ever helped them name what they can do. So you won't be asked to type a list from memory. You tap the things you've actually done, upload a résumé or a certificate, and we turn that into named skills with the evidence attached. Then we measure them against the job you're aiming at, and show you the proof level of each one and the exact next step to raise it."}
        facts={[
          { icon: A4, head: 'One tap, not a blank box', sub: 'Real experiences translated into named skills' },
          { icon: A2, head: 'Upload the proof', sub: 'Résumé, certificate, licence, transcript, award' },
          { icon: A3, head: 'Seven proof levels', sub: 'From self-claimed to verified, with the next step named' },
        ]}
        modulesTitle="What you'll do here"
        modules={[
          { icon: A4, label: 'What have you done?', line: '22 tiles across leadership, operations, people, hands-on and technical.', hue: '#34d399' },
          { icon: A2, label: 'Import from your résumé', line: 'Suggested skills with the line that proves each one, nothing saved until you confirm.', hue: '#22d3ee' },
          { icon: A3, label: 'Proof of Skill 1-7', line: 'Self-claimed, described, documented, trained, credentialed, applied, verified.', hue: '#fbbf24' },
          { icon: A5, label: 'Gap against your target', line: 'Which skills the job wants that you haven\u2019t evidenced yet.', hue: '#a78bfa' },
          { icon: A6, label: 'Evidence wallet', line: 'Your documents, private to you, downloadable and deletable any time.', hue: '#60a5fa' },
          { icon: A1, label: 'Military translation', line: 'Service experience rewritten into civilian skill language.', hue: '#f472b6' },
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <PageHeader title="Skills Intelligence"
        description="What you say you can do, what you can prove, what your target needs, and where the market is heading" />

      {/* Four layers */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4" data-testid="skills-layers">
        {LAYERS.map(([key, label, color]) => (
          <div key={key} className="glass-tint glass-cyan p-5">
            <p className={`font-outfit text-3xl font-semibold ${color}`}>{gap?.layers?.[key] ?? 0}</p>
            <p className="text-xs text-slate-300/75 mt-1">{label}</p>
          </div>
        ))}
      </div>

      {/* Target + gap */}
      <GlassCard className="mt-6">
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <div className="flex-1">
            <label className="text-xs uppercase tracking-wider text-slate-300 font-semibold">Target occupation</label>
            <Input value={target} onChange={(e) => setTarget(e.target.value)}
              onBlur={() => save(skills, target)}
              placeholder="e.g. Software Developers, Registered Nurses, Electricians"
              className="input-dark mt-2" data-testid="skills-target-input" />
          </div>
          <Button onClick={() => save(skills, target)} disabled={busy} className="btn-primary md:mt-6" data-testid="skills-set-target">
            <Target className="w-4 h-4 mr-2" />Compare
          </Button>
        </div>

        {gap && !gap.ready && (
          <p className="text-sm text-amber-200/90 mt-4" data-testid="skills-gap-blocked">{gap.message}</p>
        )}

        {gap?.ready && (
          <div className="mt-6" data-testid="skills-gap">
            <p className="text-sm text-slate-300/85">
              Compared against <span className="text-cyan-300 font-semibold">{gap.target.occupation}</span>
              {gap.target.soc_code && <span className="text-slate-300"> · SOC {gap.target.soc_code}</span>}
            </p>
            <div className="space-y-3 mt-5">
              {gap.components.map((c) => {
                const band = c.value >= 70 ? 'strong' : c.value >= 35 ? 'developing' : 'missing';
                const b = BAND[band];
                return (
                  <div key={c.key} className={`rounded-xl border p-4 ${b.ring}`} data-testid={`gap-${c.key}`}>
                    <div className="flex items-center justify-between gap-4">
                      <p className="text-sm font-semibold text-white">{c.label}</p>
                      <p className={`font-outfit text-lg font-semibold ${b.text}`}>{c.value}%</p>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/10 mt-2.5 overflow-hidden">
                      <div className={`h-full rounded-full ${b.bar}`} style={{ width: `${c.value || 0}%` }} />
                    </div>
                    <p className="text-xs text-slate-300/75 mt-2">{c.detail}</p>
                    {c.missing?.length > 0 && (
                      <p className="text-xs text-slate-300 mt-2">
                        Gaps: <span className="text-slate-200">{c.missing.join(', ')}</span>
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-300/80 mt-4 flex items-start gap-2">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />{gap.disclosure}
            </p>
          </div>
        )}
      </GlassCard>

      {/* Ranked actions */}
      {gap?.actions?.length > 0 && (
        <GlassCard className="mt-6">
          <h2 className="font-outfit text-xl font-semibold text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-cyan-300" />What to do about it
          </h2>
          <p className="text-sm text-slate-300/80 mt-1">Ranked by how much gap each closes against the time and cost it takes.</p>
          <div className="grid md:grid-cols-2 gap-3 mt-5" data-testid="skills-actions">
            {gap.actions.map((a, i) => (
              <div key={`${a.skill}-${i}`} className="glass-tint glass-violet p-4">
                <p className="text-sm font-semibold text-white">{a.skill}</p>
                <p className="text-xs text-slate-300/80 mt-1.5">{a.why}</p>
                <p className="text-xs text-slate-300/70 mt-1">{a.how}</p>
                <div className="flex flex-wrap gap-2 mt-3 text-[11px]">
                  <span className="px-2 py-0.5 rounded-full bg-white/10 text-slate-200">~{a.estimated_weeks} weeks</span>
                  <span className="px-2 py-0.5 rounded-full bg-white/10 text-slate-200">{a.cost_band} cost</span>
                  <span className="px-2 py-0.5 rounded-full bg-cyan-400/15 text-cyan-200">{a.evidence_value} evidence value</span>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Click-first skill capture, tap what you've done, or drop in a resume */}
      <GlassCard className="mt-6" data-testid="quick-build-card">
        <h2 className="font-outfit text-xl font-semibold text-white flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-cyan-300" />Build your skills without typing
        </h2>
        <p className="text-sm text-slate-300 mt-1">Tap everything you've actually done. We translate it into the skill language employers use.</p>

        <div className="mt-4 space-y-4">
          {tileGroups.map((g) => (
            <div key={g}>
              <p className="text-[11px] uppercase tracking-wider text-slate-300 mb-2">{g}</p>
              <div className="flex flex-wrap gap-2">
                {tiles.filter(t => t.group === g).map((t) => {
                  const on = pickedTiles.includes(t.key);
                  return (
                    <button key={t.key} onClick={() => setPickedTiles(p => on ? p.filter(x => x !== t.key) : [...p, t.key])}
                      data-testid={`tile-${t.key}`}
                      className={`px-3 py-2 rounded-full text-sm border transition-colors ${on
                        ? 'border-cyan-400/70 bg-cyan-500/15 text-white'
                        : 'border-white/15 bg-white/[0.04] text-slate-300 hover:border-white/40'}`}>
                      {on ? '✓ ' : ''}{t.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-5">
          <Button onClick={applyTiles} disabled={busy || pickedTiles.length === 0} className="btn-primary" data-testid="apply-tiles-btn">
            Add skills from what I've done
          </Button>
          <span className="text-xs text-slate-300">{pickedTiles.length} selected</span>
        </div>

        <div className="mt-6 pt-5 border-t border-white/10">
          <p className="text-sm text-white font-medium">Or paste your resume and we'll pull the skills out</p>
          <textarea value={resumeText} onChange={(e) => setResumeText(e.target.value)} rows={4}
            data-testid="resume-text-input"
            placeholder="Paste your resume text here, no formatting needed."
            className="mt-2 w-full input-dark" />
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <Button onClick={importResume} disabled={busy || resumeText.trim().length < 80} className="btn-secondary" data-testid="import-resume-btn">
              <Upload className="w-4 h-4 mr-1" />Pull skills from my resume
            </Button>
            <span className="text-xs text-slate-300">Nothing saves until you confirm each one.</span>
          </div>

          {suggestions.length > 0 && (
            <div className="mt-4" data-testid="resume-suggestions">
              <p className="text-sm text-white mb-2">Found {suggestions.length} skills, tap any to remove, then save.</p>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((s) => (
                  <button key={s.name} onClick={() => setSuggestions(list => list.filter(x => x.name !== s.name))}
                    title={s.evidence || ''} data-testid={`suggestion-${s.name}`}
                    className="px-3 py-1.5 rounded-full text-xs border border-emerald-400/40 bg-emerald-400/10 text-emerald-100">
                    {s.name} ✕
                  </button>
                ))}
              </div>
              <Button onClick={acceptSuggestions} disabled={busy} className="btn-primary mt-3" data-testid="accept-suggestions-btn">
                Save these {suggestions.length} skills
              </Button>
            </div>
          )}
        </div>
      </GlassCard>

      {/* Upload-first: resume, certificates and proof levels */}
      <div className="mt-6">
        <ProofOfSkill onSkillsChanged={load} />
      </div>

      {/* My skills + evidence wallet */}
      <GlassCard className="mt-6">
        <h2 className="font-outfit text-xl font-semibold text-white flex items-center gap-2">
          <Brain className="w-5 h-5 text-cyan-300" />My skills
        </h2>
        <details className="mt-4">
          <summary className="text-sm text-slate-300 cursor-pointer hover:text-white" data-testid="manual-add-toggle">Add one manually instead</summary>
          <div className="flex flex-col md:flex-row gap-2 mt-3">
          <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && addSkill()}
            placeholder="Add a skill" className="input-dark flex-1" data-testid="skill-name-input" />
          <select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}
            className="input-dark md:w-40" data-testid="skill-category-select">
            {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={draft.proficiency} onChange={(e) => setDraft({ ...draft, proficiency: e.target.value })}
            className="input-dark md:w-44" data-testid="skill-proficiency-select">
            {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{['Learning', 'Basic', 'Competent', 'Strong', 'Expert'][n - 1]}</option>)}
          </select>
          <Button onClick={addSkill} disabled={busy} className="btn-primary" data-testid="add-skill-btn">
            <Plus className="w-4 h-4 mr-1" />Add
          </Button>
          </div>
        </details>

        <div className="mt-5 space-y-2" data-testid="skills-list">
          {skills.length === 0 && <p className="text-sm text-slate-300">No skills yet. Add a few and the gap analysis appears instantly.</p>}
          {skills.map((s, i) => {
            const proven = evidencedNames.has(s.name.toLowerCase());
            return (
              <div key={`${s.name}-${i}`} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
                <span className="text-sm text-white flex-1">{s.name}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 text-slate-300">{s.category}</span>
                <span className="text-[11px] text-slate-300">{['Learning', 'Basic', 'Competent', 'Strong', 'Expert'][(s.proficiency || 3) - 1]}</span>
                {proven ? (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-400/15 text-emerald-300 inline-flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" />Proven
                  </span>
                ) : (
                  <button onClick={() => addEvidence(s.name)} data-testid={`prove-${s.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/15 transition-colors duration-200">
                    Add proof
                  </button>
                )}
                <button onClick={() => save(skills.filter((_, j) => j !== i))} className="text-slate-300 hover:text-rose-300 transition-colors duration-200">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </GlassCard>

      {evidence.length > 0 && (
        <GlassCard className="mt-6">
          <h2 className="font-outfit text-xl font-semibold text-white flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-emerald-300" />Evidence Wallet
          </h2>
          <p className="text-sm text-slate-300/80 mt-1">Proof turns a claim into something an employer can verify.</p>
          <div className="mt-4 space-y-2" data-testid="evidence-list">
            {evidence.map((e) => (
              <div key={e.id} className="flex items-center gap-3 rounded-xl border border-emerald-400/20 bg-emerald-400/[0.06] px-4 py-3">
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-400/15 text-emerald-300">{e.skill}</span>
                <span className="text-sm text-slate-200 flex-1">{e.title}</span>
                <button onClick={() => removeEvidence(e.id)} className="text-slate-300 hover:text-rose-300 transition-colors duration-200">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Military grade + MOS crosswalk */}
      {milRef && (
        <GlassCard className="mt-6" data-testid="military-crosswalk-card">
          <h2 className="font-outfit text-xl font-semibold text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-violet-300" />Military grade &amp; MOS crosswalk
          </h2>
          <p className="text-sm text-slate-300/80 mt-1">
            You know your pay grade and your MOS. That's enough, we'll map both to civilian seniority
            and the actual job titles they lead to.
          </p>
          <div className="grid md:grid-cols-3 gap-3 mt-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-300 font-semibold">Pay grade</label>
              <select value={grade} onChange={(e) => setGrade(e.target.value)} className="input-dark w-full mt-2" data-testid="pay-grade-select">
                <option value="">Select grade</option>
                {milRef.pay_grades.map((g) => (
                  <option key={g.grade} value={g.grade}>{g.grade}, {g.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-300 font-semibold">MOS / AFSC / Rating</label>
              <select value={mos} onChange={(e) => setMos(e.target.value)} className="input-dark w-full mt-2" data-testid="mos-select">
                <option value="">Select or leave blank</option>
                {milRef.codes.map((c) => (
                  <option key={c.code} value={c.code}>{c.code}, {c.name} ({c.branch})</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <Button onClick={runCrosswalk} disabled={busy} className="btn-primary w-full" data-testid="crosswalk-btn">
                Map my service <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-300/80 mt-2">{milRef.note}</p>

          {crosswalk && (
            <div className="mt-5 space-y-4" data-testid="crosswalk-result">
              {crosswalk.seniority && (
                <div className="rounded-xl border border-emerald-400/30 bg-emerald-400/[0.08] p-5">
                  <p className="text-xs uppercase tracking-wider text-emerald-200 font-semibold">Civilian seniority</p>
                  <p className="font-outfit text-2xl font-semibold text-white mt-2">{crosswalk.seniority.civilian_level}</p>
                  <p className="text-sm text-slate-200/85 mt-2">{crosswalk.seniority.message}</p>
                  {crosswalk.seniority.warning && (
                    <p className="text-sm text-amber-200 mt-3 font-medium">{crosswalk.seniority.warning}</p>
                  )}
                </div>
              )}
              {crosswalk.career_titles?.length > 0 ? (
                <div>
                  <p className="text-xs uppercase tracking-wider text-violet-200 font-semibold mb-3">
                    Civilian job titles this leads to
                    {crosswalk.mos && <span className="text-slate-300 normal-case tracking-normal">, from {crosswalk.mos.code} {crosswalk.mos.name}, {crosswalk.mos.branch}</span>}
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {crosswalk.career_titles.map((c) => (
                      <button key={c.soc_code} onClick={() => { setTarget(c.title); save(skills, c.title); }}
                        data-testid={`crosswalk-title-${c.soc_code}`}
                        className="glass-tint glass-violet p-4 text-left group">
                        <p className="text-sm font-semibold text-white group-hover:text-violet-200 transition-colors duration-200">{c.title}</p>
                        <p className="text-xs text-slate-300/70 mt-1">{c.field} · SOC {c.soc_code}</p>
                        <p className="text-[11px] text-violet-200 mt-2">Set as my target →</p>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-amber-200/90">{crosswalk.guidance}</p>
              )}
            </div>
          )}
        </GlassCard>
      )}

      {/* Military translator */}
      <GlassCard className="mt-6">
        <h2 className="font-outfit text-xl font-semibold text-white flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-violet-300" />Translate military or prior experience
        </h2>
        <p className="text-sm text-slate-300/80 mt-1">
          Describe what you were responsible for in your own words. We map it to civilian skills a recruiter recognises.
        </p>
        <textarea value={duties} onChange={(e) => setDuties(e.target.value)} rows={3}
          placeholder="e.g. Served as squad leader responsible for logistics and supply accountability; trained junior soldiers"
          className="input-dark w-full mt-4" data-testid="military-duties-input" />
        <Button onClick={translate} disabled={busy} className="btn-primary mt-3" data-testid="translate-btn">
          Translate <ArrowRight className="w-4 h-4 ml-2" />
        </Button>

        {translation && (
          <div className="mt-5 rounded-xl border border-violet-400/30 bg-violet-400/[0.08] p-5" data-testid="translation-result">
            {translation.unmatched ? (
              <p className="text-sm text-amber-200/90">{translation.guidance}</p>
            ) : (
              <>
                <p className="text-xs uppercase tracking-wider text-violet-200 font-semibold">Civilian skills identified</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {translation.civilian_skills.map((s) => (
                    <span key={s} className="text-xs px-3 py-1 rounded-full bg-white/10 text-slate-100">{s}</span>
                  ))}
                </div>
                {translation.seniority_note && (
                  <p className="text-sm text-emerald-200 mt-4">{translation.seniority_note}</p>
                )}
                <Button onClick={adoptTranslated} className="btn-primary mt-4" data-testid="adopt-translated-btn">
                  Add these to my skills
                </Button>
              </>
            )}
          </div>
        )}
      </GlassCard>

      {/* Emerging skills radar */}
      {emerging && (
        <GlassCard className="mt-6 mb-10">
          <h2 className="font-outfit text-xl font-semibold text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-amber-300" />Where the market is moving
          </h2>
          <p className="text-xs text-slate-300/85 mt-1">{emerging.note}</p>
          <div className="grid md:grid-cols-2 gap-6 mt-5" data-testid="emerging-radar">
            <div>
              <p className="text-xs uppercase tracking-wider text-emerald-300 font-semibold mb-3">Rising</p>
              <div className="space-y-2">
                {emerging.rising.map((s) => (
                  <div key={s.skill} className="rounded-lg border border-emerald-400/20 bg-emerald-400/[0.06] px-3 py-2.5">
                    <p className="text-sm text-white">{s.skill}</p>
                    <p className="text-[11px] text-slate-300 mt-0.5">{s.source} · {s.published}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-rose-300 font-semibold mb-3 flex items-center gap-1.5">
                <TrendingDown className="w-3.5 h-3.5" />Declining
              </p>
              <div className="space-y-2">
                {emerging.declining.map((s) => (
                  <div key={s.skill} className="rounded-lg border border-rose-400/20 bg-rose-400/[0.06] px-3 py-2.5">
                    <p className="text-sm text-white">{s.skill}</p>
                    <p className="text-[11px] text-slate-300 mt-0.5">{s.source} · {s.published}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </GlassCard>
      )}
    </AppLayout>
  );
}
